import { createSlice } from "@reduxjs/toolkit";
import {
  AccountDimension,
  Co2Month,
  EntityAccount,
  GeoRow,
  InsightProductOption,
  InvoicingStats,
  MonthlyTotal,
  NamedValue,
  OrderRow,
  PeriodSummary,
  ProductMixRow,
  UtilizationRow,
} from "dora-contracts";
import { createErrorReportingAsyncThunk } from "../../helpers";
import * as api from "../../../pages/InsightsPage/insights-api";
import * as costsApi from "../../../pages/InsightsPage/insights-costs-api";

const prefix = "data/insights";

type State = {
  summary: PeriodSummary | null;
  orders: OrderRow[];
  // Keyed by dimension: the main page (one dimension at a time) and the
  // charts view (CLIENT + TRUCK + DRIVER concurrently) both read this, so a
  // single array would have them clobber each other's data.
  accountsByDimension: Partial<Record<AccountDimension, EntityAccount[]>>;
  monthly: MonthlyTotal[];
  co2: { monthly: Co2Month[]; byVehicle: NamedValue[] };
  geography: { byCountry: GeoRow[]; byCity: GeoRow[] };
  invoicing: InvoicingStats | null;
  productMix: ProductMixRow[];
  utilization: UtilizationRow[];
  products: InsightProductOption[];
};

const initialState: State = {
  summary: null,
  orders: [],
  accountsByDimension: {},
  monthly: [],
  co2: { monthly: [], byVehicle: [] },
  geography: { byCountry: [], byCity: [] },
  invoicing: null,
  productMix: [],
  utilization: [],
  products: [],
};

export const loadInsightsSummary = createErrorReportingAsyncThunk(
  `${prefix}/load-summary`,
  async (input: { from: string; to: string }) =>
    api.getInsightsSummary(input.from, input.to)
);

export const loadInsightsOrders = createErrorReportingAsyncThunk(
  `${prefix}/load-orders`,
  async (input: { from: string; to: string }) =>
    api.getInsightsOrders(input.from, input.to)
);

export const loadInsightsAccounts = createErrorReportingAsyncThunk(
  `${prefix}/load-accounts`,
  async (input: { dimension: AccountDimension; from: string; to: string }) => {
    const accounts = await api.getInsightsAccounts(input.dimension, input.from, input.to);
    return { dimension: input.dimension, accounts };
  }
);

export const loadInsightsMonthly = createErrorReportingAsyncThunk(
  `${prefix}/load-monthly`,
  async (input: { firstMonth: string; lastMonth: string }) =>
    api.getInsightsMonthly(input.firstMonth, input.lastMonth)
);

export const loadInsightsCo2 = createErrorReportingAsyncThunk(
  `${prefix}/load-co2`,
  async (input: {
    from: string;
    to: string;
    firstMonth: string;
    lastMonth: string;
  }) => api.getInsightsCo2(input.from, input.to, input.firstMonth, input.lastMonth)
);

export const loadInsightsGeography = createErrorReportingAsyncThunk(
  `${prefix}/load-geography`,
  async (input: { from: string; to: string }) =>
    api.getInsightsGeography(input.from, input.to)
);

export const loadInsightsInvoicing = createErrorReportingAsyncThunk(
  `${prefix}/load-invoicing`,
  async (input: { from: string; to: string }) =>
    api.getInsightsInvoicing(input.from, input.to)
);

export const loadInsightsProductMix = createErrorReportingAsyncThunk(
  `${prefix}/load-product-mix`,
  async (input: { from: string; to: string }) =>
    api.getInsightsProductMix(input.from, input.to)
);

export const loadInsightsUtilization = createErrorReportingAsyncThunk(
  `${prefix}/load-utilization`,
  async (input: { from: string; to: string }) =>
    api.getInsightsUtilization(input.from, input.to)
);

export const loadInsightsProducts = createErrorReportingAsyncThunk(
  `${prefix}/load-products`,
  async () => costsApi.listProducts()
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadInsightsSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
      })
      .addCase(loadInsightsOrders.fulfilled, (state, action) => {
        state.orders = action.payload;
      })
      .addCase(loadInsightsAccounts.fulfilled, (state, action) => {
        state.accountsByDimension[action.payload.dimension] = action.payload.accounts;
      })
      .addCase(loadInsightsMonthly.fulfilled, (state, action) => {
        state.monthly = action.payload;
      })
      .addCase(loadInsightsCo2.fulfilled, (state, action) => {
        state.co2 = action.payload;
      })
      .addCase(loadInsightsGeography.fulfilled, (state, action) => {
        state.geography = action.payload;
      })
      .addCase(loadInsightsInvoicing.fulfilled, (state, action) => {
        state.invoicing = action.payload;
      })
      .addCase(loadInsightsProductMix.fulfilled, (state, action) => {
        state.productMix = action.payload;
      })
      .addCase(loadInsightsUtilization.fulfilled, (state, action) => {
        state.utilization = action.payload;
      })
      .addCase(loadInsightsProducts.fulfilled, (state, action) => {
        state.products = action.payload;
      });
  },
});

export default slice.reducer;
